<template>
  <NcrmRecordLayout :left-lg-cols="3" :main-lg-cols-both="7" :right-lg-cols="2">
    <template #header>
      <NcrmRecordHeader
        :label="$t('ncrm.contacts.details.recordLabel')"
        :title="recordTitle"
        :chips="headerChips"
        :kpis="headerKpis"
        :clickable-kpi-keys="headerClickableKpiKeys"
        show-back-button
        :back-aria-label="$t('app.actions.back')"
        @back-click="goBackToContacts"
        @kpi-click="handleHeaderKpiClick"
      >
        <template #actions>
          <NcrmContactHeaderActions
            entity-type="company"
            :entity-id="recordId"
            ai-profile-key="enrich_research"
            :ai-label="$t('ncrm.ai.actions.companyEnrich')"
            :disabled="!record"
            :recipient-email="record?.email_primary || ''"
            :recipient-name="recordTitle"
            @error="showSnackbar($event, 'error')"
            @notify="onMailjetNotify"
            @email-sent="handleCompanyEmailSent"
            @add-activity="openAddActivity"
            @edit="openEditDialog"
            @delete="openDeleteDialog"
          >
            <!-- Organizare: board-urile consolidate într-un singur „Adaugă în board ▾" -->
            <template #organize>
              <v-menu location="bottom end">
                <template #activator="{ props }">
                  <v-btn v-bind="props" color="tertiary" variant="tonal" size="default" :disabled="!record">
                    <v-icon start>mdi-view-column-outline</v-icon>
                    {{ $t("ncrm.contacts.details.actions.addToBoard") }}
                    <v-icon end>mdi-chevron-down</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact" class="py-1">
                  <v-list-item :disabled="!record" @click="openAddToLeadBoard">
                    <template #prepend><v-icon>mdi-account-plus-outline</v-icon></template>
                    <v-list-item-title>{{ $t("ncrm.contacts.details.actions.addToLeadBoard") }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item :disabled="!record" @click="openAddToDealBoard">
                    <template #prepend><v-icon>mdi-cash-plus</v-icon></template>
                    <v-list-item-title>{{ $t("ncrm.contacts.details.actions.addToDealBoard") }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
            <!-- Mobil: aceleași board-uri în meniul ⋮ -->
            <template #organizeMenu>
              <v-list-item :disabled="!record" @click="openAddToLeadBoard">
                <template #prepend><v-icon>mdi-account-plus-outline</v-icon></template>
                <v-list-item-title>{{ $t("ncrm.contacts.details.actions.addToLeadBoard") }}</v-list-item-title>
              </v-list-item>
              <v-list-item :disabled="!record" @click="openAddToDealBoard">
                <template #prepend><v-icon>mdi-cash-plus</v-icon></template>
                <v-list-item-title>{{ $t("ncrm.contacts.details.actions.addToDealBoard") }}</v-list-item-title>
              </v-list-item>
            </template>
          </NcrmContactHeaderActions>
        </template>
      </NcrmRecordHeader>
    </template>
    <template #left>
      <NcrmRecordPropertiesCard
        :title="$t('ncrm.contacts.details.sections.companyProperties')"
        :loading="loading"
        :sections="companyPropertySectionsForDisplay"
        :skeleton-groups="propertySkeletonGroups"
        :empty-label="$t('ncrm.contacts.details.propertyEmpty.notSet')"
        :empty-sections-label="$t('ncrm.contacts.details.propertySections.empty')"
        :copy-aria-label="$t('ncrm.contacts.details.propertyInteractions.copy')"
        show-settings-button
        :settings-disabled="!record"
        :settings-aria-label="$t('ncrm.contacts.details.propertyLayout.actions.open')"
        :resolve-row-link="resolvePropertyRowLink"
        :privacy-subject="{ type: 'company', id: recordId }"
        :resolve-row-tooltip="resolveTimestampTooltip"
        :can-copy-row="canCopyPropertyRow"
        @settings-click="openPropertyLayoutDrawer"
        @copy-row="copyPropertyRowValue"
        @empty-action="handlePropertySectionEmptyAction"
      />
    </template>
    <template #main>
      <v-card class="pa-4">
        <div class="text-subtitle-1 ncrm-record-title">
          {{ $t("ncrm.contacts.details.sections.mainWorkspace") }}
        </div>
        <v-progress-linear v-if="loading" indeterminate color="primary" class="mt-4" />
        <div v-show="!loading" class="mt-3">
          <v-tabs v-model="mainTab" color="primary" density="comfortable" show-arrows class="ncrm-main-workspace-tabs">
            <v-tab value="overview">{{ $t("ncrm.contacts.details.tabs.overview") }}</v-tab>
            <v-tab value="mailjet">{{ $t("ncrm.integrations.mailjet.activity.tab") }}</v-tab>
            <v-tab value="activity">{{ $t("ncrm.contacts.details.tabs.activity") }}</v-tab>
            <v-tab value="data-protection">{{ $t("ncrm.contacts.dataProtection.header") }}</v-tab>
            <v-tab value="history">{{ $t("ncrm.contacts.details.tabs.history") }}</v-tab>
            <v-tab value="people">{{ $t("ncrm.contacts.details.tabs.people") }}</v-tab>
          </v-tabs>
          <v-window v-model="mainTab" class="ncrm-main-workspace-window" :touch="false">
            <v-window-item value="overview">
              <NcrmPrivacyOverviewStatus subject-type="company" :subject-id="recordId" :active="mainTab === 'overview'" @activate="mainTab = 'data-protection'" />
              <div v-if="hasOverviewBoardItems" class="ncrm-overview-board-sections">
                <section
                  v-for="section in nonEmptyOverviewSections"
                  :key="`company-overview-section-${section.key}`"
                >
                  <div class="text-caption text-medium-emphasis mb-2">
                    {{ section.title }}
                  </div>
                  <v-list
                    lines="two"
                    density="comfortable"
                    class="ncrm-overview-board-list pa-0"
                  >
                    <v-list-item
                      v-for="item in section.items"
                      :key="buildOverviewItemKey(item)"
                      :to="item.route || undefined"
                      :link="Boolean(item.route)"
                      :ripple="Boolean(item.route)"
                      :class="[
                        'ncrm-overview-board-list__item',
                        { 'ncrm-overview-board-list__item--clickable': Boolean(item.route) },
                      ]"
                    >
                      <v-list-item-title>{{ formatOverviewItemTitle(item) }}</v-list-item-title>
                      <v-list-item-subtitle>{{ formatOverviewItemSubtitle(item) }}</v-list-item-subtitle>
                      <template #append>
                        <div class="d-flex align-center ga-2">
                          <v-chip
                            size="x-small"
                            :color="resolveOverviewStatusColor(item.sourceType, item.status)"
                          >
                            {{ formatOverviewStatus(item.sourceType, item.status) }}
                          </v-chip>
                          <v-icon v-if="item.route" size="18" class="text-medium-emphasis">
                            mdi-chevron-right
                          </v-icon>
                        </div>
                      </template>
                    </v-list-item>
                  </v-list>
                </section>
              </div>
              <div v-else class="text-body-2 text-medium-emphasis">
                {{ $t("ncrm.contacts.details.overview.empty") }}
              </div>
            </v-window-item>
            <v-window-item value="mailjet">
              <NcrmMailjetActivityPanel v-if="mainTab === 'mailjet'" entity-type="company"
                :entity-id="recordId" @notify="onMailjetNotify" />
            </v-window-item>
            <v-window-item value="activity">
              <NcrmActivityTimeline
                v-if="mainTab === 'activity'"
                :key="`company-activity-${recordId}-${activityRefreshKey}`"
                entity-type="company"
                :entity-id="recordId" scope="customer"
                :composer-open="activityComposerOpen"
                @update:composer-open="activityComposerOpen = $event"
              />
            </v-window-item>
            <v-window-item value="data-protection" eager>
              <NcrmPrivacyRecordTab v-if="privacyTabVisited" subject-type="company" :subject-id="recordId" /></v-window-item>
            <v-window-item value="history">
              <NcrmRecordHistoryTimeline
                v-if="mainTab === 'history'"
                entity-type="company"
                :entity-id="recordId"
              />
            </v-window-item>
            <v-window-item value="people">
              <div class="d-flex flex-wrap align-center justify-space-between ga-2 mb-2">
                <div class="text-caption text-medium-emphasis">
                  {{
                    $t("ncrm.contacts.details.company.people.linkedCount", {
                      count: linkedPeopleTotalLabel,
                    })
                  }}
                </div>
                <div class="d-flex flex-wrap ga-2">
                  <v-btn
                    size="small"
                    color="primary"
                    variant="outlined"
                    :disabled="!record"
                    @click="openPeopleAssocDrawer('existing')"
                  >
                    <v-icon start size="16">mdi-account-plus-outline</v-icon>
                    {{ $t("ncrm.contacts.details.company.people.addExisting") }}
                  </v-btn>
                  <v-btn
                    size="small"
                    color="primary"
                    variant="text"
                    :disabled="!record"
                    @click="openPeopleAssocDrawer('create')"
                  >
                    <v-icon start size="16">mdi-plus</v-icon>
                    {{ $t("ncrm.contacts.details.company.people.createNew") }}
                  </v-btn>
                </div>
              </div>
              <v-progress-linear v-if="linkedPeopleLoading" indeterminate color="primary" class="mt-2" />
              <div v-else-if="linkedPeople.length">
                <v-list lines="two" density="comfortable" class="ncrm-linked-people-list pa-0">
                  <v-list-item
                    v-for="person in linkedPeople"
                    :key="`linked-person-${person.id}`"
                    class="ncrm-linked-people-list__item"
                    @click="openLinkedPerson(person)"
                  >
                    <template #prepend>
                      <v-avatar size="28" color="primary" variant="tonal">
                        <v-icon size="16">mdi-account</v-icon>
                      </v-avatar>
                    </template>
                    <v-list-item-title>
                      {{ formatLinkedPersonName(person) }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ formatLinkedPersonSubtitle(person) }}
                    </v-list-item-subtitle>
                    <template #append>
                      <div class="d-flex align-center ga-1">
                        <v-btn
                          size="x-small"
                          variant="text"
                          color="error"
                          :loading="unlinkPeopleLoadingId === Number(person.id)"
                          @click.stop="unlinkPersonFromCompany(person)"
                        >
                          {{ $t("ncrm.contacts.details.company.people.unlink") }}
                        </v-btn>
                        <v-icon size="18" class="text-medium-emphasis">mdi-chevron-right</v-icon>
                      </div>
                    </template>
                  </v-list-item>
                </v-list>
              </div>
              <div v-else class="text-body-2 text-medium-emphasis">
                {{ $t("ncrm.contacts.details.company.people.empty") }}
              </div>
            </v-window-item>
          </v-window>
        </div>
      </v-card>
    </template>
    <template #right>
      <v-card class="pa-4">
        <div class="text-subtitle-1 ncrm-record-title">
          {{ $t("ncrm.contacts.details.sections.context") }}
        </div>
        <v-progress-linear v-if="loading" indeterminate color="primary" class="mt-4" />
        <div v-else class="mt-3">
          <div class="d-flex align-center justify-space-between ga-2 mb-2">
            <div class="text-caption text-medium-emphasis ncrm-context-section-title">
              {{
                $t("ncrm.contacts.details.company.people.contextTitle", {
                  count: linkedPeopleTotalLabel,
                })
              }}
            </div>
            <v-btn
              size="x-small"
              variant="text"
              color="primary"
              icon="mdi-pencil"
              :aria-label="$t('ncrm.contacts.details.company.people.contextEditAction')"
              :disabled="!record"
              @click="openPeopleTabFromContext"
            />
          </div>
          <v-progress-linear v-if="linkedPeopleLoading" indeterminate color="primary" class="mb-2" />
          <v-list v-else-if="contextPeoplePreview.length" lines="two" density="compact" class="ncrm-context-people pa-0">
            <v-list-item
              v-for="person in contextPeoplePreview"
              :key="`context-person-${person.id}`"
              class="ncrm-context-people__item"
              @click="openLinkedPerson(person)"
            >
              <template #prepend>
                <v-avatar size="24" color="primary" variant="tonal">
                  <v-icon size="14">mdi-account</v-icon>
                </v-avatar>
              </template>
              <v-list-item-title class="text-body-2">
                {{ formatLinkedPersonName(person) }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ formatLinkedPersonSubtitle(person) }}
              </v-list-item-subtitle>
              <template #append>
                <v-icon size="16" class="text-medium-emphasis">mdi-chevron-right</v-icon>
              </template>
            </v-list-item>
          </v-list>
          <div v-else class="text-body-2 text-medium-emphasis">
            {{ $t("ncrm.contacts.details.company.people.empty") }}
          </div>
          <v-btn
            v-if="contextPeopleHasMore"
            size="small"
            variant="text"
            color="primary"
            class="mt-2 px-0"
            @click="openPeopleTabFromContext"
          >
            {{ $t("ncrm.contacts.details.company.people.contextViewAllAction") }}
          </v-btn>
          <div class="ncrm-context-section-sep" />
          <div class="text-caption text-medium-emphasis ncrm-context-section-title mb-1">
            {{ $t("ncrm.contacts.details.company.fields.customFields") }}
          </div>
          <div v-if="customFieldsForDisplay.length" class="ncrm-details-list">
            <div
              v-for="entry in customFieldsForDisplay"
              :key="`context-custom-field-${entry.field_definition_id}`"
              class="ncrm-details-list__item"
            >
              <div class="text-caption text-medium-emphasis">{{ entry.label }}</div>
              <div class="text-body-2 ncrm-details-list__value">
                {{ entry.value_text || emptyValueLabel }}
              </div>
            </div>
          </div>
          <div v-else class="text-body-2 text-medium-emphasis">
            {{ $t("ncrm.contacts.details.company.emptyCustomFields") }}
          </div>
        </div>
      </v-card>
    </template>
  </NcrmRecordLayout>
  <NcrmRecordEditDialog
    v-model="editDialog"
    :title="$t('ncrm.contacts.details.company.dialogs.editTitle')"
    :subtitle="recordTitle"
    :loading="editLoading"
    :extra-action-label="$t('ncrm.contacts.details.dialogs.completeAllValues')"
    :extra-action-disabled="editShowAllFields"
    @extra-action="expandEditFormFields"
    @save="submitEdit"
  >
    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="editForm.name"
          :label="$t('ncrm.contacts.details.company.fields.name')"
          required
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="editForm.business_category"
          :label="$t('ncrm.contacts.details.company.fields.businessCategory')"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-select
          v-model="editForm.business_is_active"
          :items="businessActiveOptions"
          item-title="label"
          item-value="value"
          :label="$t('ncrm.contacts.details.businessStatus.fieldLabel')"
          :menu-props="selectMenuProps"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="editForm.email_primary"
          :label="$t('ncrm.contacts.details.company.fields.emailPrimary')"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="editForm.phone_primary"
          :label="$t('ncrm.contacts.details.company.fields.phonePrimary')"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="editForm.website"
          :label="$t('ncrm.contacts.details.company.fields.website')"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="editForm.source"
          :label="$t('ncrm.contacts.details.company.fields.source')"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="editForm.country"
          :label="$t('ncrm.contacts.details.company.fields.country')"
        />
      </v-col>
      <template v-if="editShowAllFields">
        <v-col cols="12">
          <div class="text-caption text-medium-emphasis">
            {{ $t("ncrm.contacts.details.dialogs.additionalFields") }}
          </div>
        </v-col>
        <v-col
          v-for="field in companyAdvancedEditFields"
          :key="`company-edit-field-${field.key}`"
          cols="12"
          md="6"
        >
          <component
            :is="field.component"
            v-model="editForm[field.key]"
            v-bind="field.props"
            variant="outlined"
            density="comfortable"
          />
        </v-col>
      </template>
      <v-col cols="12">
        <v-textarea
          v-model="editForm.address_text"
          :label="$t('ncrm.contacts.details.company.fields.address')"
          rows="2"
          auto-grow
        />
      </v-col>
      <v-col cols="12">
        <v-textarea
          v-model="editForm.notes"
          :label="$t('ncrm.contacts.details.company.fields.notes')"
          rows="3"
          auto-grow
        />
      </v-col>
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-2">
          <div class="text-caption text-medium-emphasis">
            {{ $t("ncrm.contacts.details.company.fields.customFields") }}
          </div>
          <v-btn
            size="x-small"
            variant="text"
            color="primary"
            class="px-0"
            prepend-icon="mdi-plus"
            @click="addEditCustomFieldRow"
          >
            {{ $t("ncrm.contacts.details.customFieldEditor.addAction") }}
          </v-btn>
        </div>
        <v-row>
          <v-col
            v-for="entry in editExistingCustomFields"
            :key="`company-edit-existing-custom-field-${entry.field_definition_id}`"
            cols="12"
          >
            <v-row dense class="ncrm-custom-field-editor-row">
              <v-col cols="12" md="11">
                <v-text-field
                  v-model="editCustomFieldValues[entry.field_definition_id]"
                  :label="resolveEditCustomFieldLabel(entry)"
                />
              </v-col>
              <v-col cols="12" md="1" class="d-flex align-center justify-end pb-md-4">
                <v-btn
                  icon="mdi-delete-outline"
                  size="small"
                  variant="text"
                  color="error"
                  :aria-label="$t('ncrm.contacts.details.customFieldEditor.removeAction')"
                  @click="removeExistingEditCustomField(entry.field_definition_id)"
                />
              </v-col>
            </v-row>
          </v-col>
          <v-col
            v-for="entry in editNewCustomFields"
            :key="`company-edit-new-custom-field-${entry.temp_id}`"
            cols="12"
          >
            <v-row dense class="ncrm-custom-field-editor-row">
              <v-col cols="12" md="5">
                <v-text-field
                  v-model="entry.label"
                  :label="$t('ncrm.contacts.details.customFieldEditor.nameLabel')"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="entry.value"
                  :label="$t('ncrm.contacts.details.customFieldEditor.valueLabel')"
                />
              </v-col>
              <v-col cols="12" md="1" class="d-flex align-center justify-end pb-md-4">
                <v-btn
                  icon="mdi-close"
                  size="small"
                  variant="text"
                  color="error"
                  :aria-label="$t('ncrm.contacts.details.customFieldEditor.removeAction')"
                  @click="removeEditCustomFieldRow(entry.temp_id)"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </NcrmRecordEditDialog>
  <NcrmRecordDeleteDialog
    v-model="deleteDialog"
    v-model:confirmText="deleteConfirmText"
    :title="$t('ncrm.contacts.details.company.dialogs.deleteTitle')"
    :hint="$t('ncrm.contacts.details.company.dialogs.deleteHint')"
    :confirm-label="$t('ncrm.contacts.details.dialogs.confirmDeleteLabel')"
    :action-text="$t('ncrm.contacts.details.actions.delete')"
    :loading="deleteLoading"
    @confirm="submitDelete"
  />
  <NcrmPropertyLayoutDrawer
    v-model="propertyLayoutDrawer"
    :title="$t('ncrm.contacts.details.propertyLayout.title')"
    :subtitle="$t('ncrm.contacts.details.propertyLayout.subtitle')"
    :items="companyPropertyLayoutItems"
    :visible-keys="companyVisiblePropertyKeys"
    :loading="propertyLayoutLoading"
    :saving="propertyLayoutSaving"
    @save="savePropertyLayout"
  />
  <NcrmAddToBoardDrawer
    v-model="addToBoardDrawer.open"
    :mode="addToBoardDrawer.mode"
    :company-id="recordId"
    :company-name="recordTitle"
    @added="handleAddToBoardSuccess"
    @error="showSnackbar($event, 'error')"
  />
  <v-navigation-drawer
    v-model="peopleAssocDrawer"
    location="right"
    temporary
    width="460"
    class="ncrm-assoc-drawer"
  >
    <div class="d-flex flex-column fill-height">
      <div class="d-flex align-center justify-space-between pa-4">
        <div class="text-h6">{{ $t("ncrm.contacts.details.company.people.drawerTitle") }}</div>
        <v-btn icon="mdi-close" variant="text" @click="closePeopleAssocDrawer" />
      </div>
      <v-divider />
      <v-tabs v-model="peopleAssocTab" color="primary" grow class="px-2 pt-2">
        <v-tab value="existing">{{ $t("ncrm.contacts.details.company.people.addExisting") }}</v-tab>
        <v-tab value="create">{{ $t("ncrm.contacts.details.company.people.createNew") }}</v-tab>
      </v-tabs>
      <v-window v-model="peopleAssocTab" class="flex-grow-1 overflow-y-auto px-4 pt-4" :touch="false">
        <v-window-item value="existing">
          <v-text-field
            v-model="peopleSearchQuery"
            :label="$t('ncrm.contacts.details.company.people.searchPlaceholder')"
            clearable
            prepend-inner-icon="mdi-magnify"
          />
          <div class="text-caption text-medium-emphasis mb-2">
            {{
              $t("ncrm.contacts.details.company.people.selectedCount", {
                count: selectedPeopleCount,
              })
            }}
          </div>
          <v-progress-linear v-if="peopleSearchLoading" indeterminate color="primary" class="mb-2" />
          <v-list v-else-if="peopleSearchItems.length" lines="two" density="comfortable" class="pa-0">
            <v-list-item
              v-for="person in peopleSearchItems"
              :key="`people-search-${person.id}`"
              class="ncrm-assoc-drawer__list-item"
              @click="togglePeopleSelection(person)"
            >
              <template #prepend>
                <v-checkbox-btn
                  :model-value="isPersonSelected(person.id)"
                  color="primary"
                  @click.stop="togglePeopleSelection(person)"
                />
              </template>
              <v-list-item-title>{{ formatLinkedPersonName(person) }}</v-list-item-title>
              <v-list-item-subtitle>{{ formatSearchPersonSubtitle(person) }}</v-list-item-subtitle>
            </v-list-item>
          </v-list>
          <div v-else class="text-body-2 text-medium-emphasis">
            {{ $t("ncrm.contacts.details.company.people.searchEmpty") }}
          </div>
        </v-window-item>
        <v-window-item value="create">
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="createPersonForm.first_name"
                :label="$t('ncrm.contacts.details.person.fields.firstName')"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="createPersonForm.last_name"
                :label="$t('ncrm.contacts.details.person.fields.lastName')"
                required
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="createPersonForm.email_primary"
                :label="$t('ncrm.contacts.details.person.fields.emailPrimary')"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="createPersonForm.phone_primary"
                :label="$t('ncrm.contacts.details.person.fields.phonePrimary')"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="createPersonForm.country"
                :label="$t('ncrm.contacts.details.person.fields.country')"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="createPersonForm.position"
                :label="$t('ncrm.contacts.details.person.fields.position')"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="createPersonForm.department"
                :label="$t('ncrm.contacts.details.person.fields.department')"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="createPersonForm.source"
                :label="$t('ncrm.contacts.details.person.fields.source')"
              />
            </v-col>
          </v-row>
        </v-window-item>
      </v-window>
      <v-divider />
      <div class="d-flex justify-end ga-2 pa-4">
        <v-btn variant="text" @click="closePeopleAssocDrawer">
          {{ $t("app.actions.cancel") }}
        </v-btn>
        <v-btn
          v-if="peopleAssocTab === 'existing'"
          color="primary"
          variant="elevated"
          :loading="peopleLinkLoading"
          :disabled="selectedPeopleCount === 0"
          @click="submitLinkSelectedPeople"
        >
          {{ $t("app.actions.save") }}
        </v-btn>
        <v-btn
          v-else
          color="primary"
          variant="elevated"
          :loading="createPersonLoading"
          @click="submitCreateLinkedPerson"
        >
          {{ $t("app.actions.save") }}
        </v-btn>
      </div>
    </div>
  </v-navigation-drawer>
  <v-dialog v-model="peopleMoveConfirmDialog" max-width="520">
    <v-card>
      <v-card-title>{{ $t("ncrm.contacts.details.company.people.moveConfirmTitle") }}</v-card-title>
      <v-card-text>
        <div class="text-body-2 mb-3">
          {{ $t("ncrm.contacts.details.company.people.moveConfirmHint") }}
        </div>
        <v-list density="compact" class="py-0">
          <v-list-item
            v-for="person in pendingMovePeople"
            :key="`move-person-${person.id}`"
            class="px-0"
          >
            <v-list-item-title>{{ formatLinkedPersonName(person) }}</v-list-item-title>
            <v-list-item-subtitle>{{ formatSearchPersonSubtitle(person) }}</v-list-item-subtitle>
          </v-list-item>
        </v-list>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn variant="text" @click="peopleMoveConfirmDialog = false">
          {{ $t("app.actions.cancel") }}
        </v-btn>
        <v-btn color="warning" variant="elevated" :loading="peopleLinkLoading" @click="confirmMoveAndLinkPeople">
          {{ $t("ncrm.contacts.details.company.people.moveConfirmAction") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-snackbar v-model="snackbar.show" :timeout="3200" :color="snackbar.color">
    {{ snackbar.message }}
  </v-snackbar>
</template>
<script>
import axios from "axios";
import NcrmRecordHeader from "@/components/ncrm/shared/NcrmRecordHeader.vue";
import NcrmRecordLayout from "@/components/ncrm/shared/NcrmRecordLayout.vue";
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";
import NcrmRecordDeleteDialog from "@/components/ncrm/shared/NcrmRecordDeleteDialog.vue";
import NcrmActivityTimeline from "@/components/ncrm/shared/NcrmActivityTimeline.vue";
import NcrmPropertyLayoutDrawer from "@/components/ncrm/shared/NcrmPropertyLayoutDrawer.vue";
import NcrmContactHeaderActions from "@/components/ncrm/shared/NcrmContactHeaderActions.vue";
import NcrmAddToBoardDrawer from "@/components/ncrm/shared/NcrmAddToBoardDrawer.vue";
import NcrmRecordPropertiesCard from "@/components/ncrm/shared/NcrmRecordPropertiesCard.vue";
import NcrmRecordHistoryTimeline from "@/components/ncrm/shared/NcrmRecordHistoryTimeline.vue";
import NcrmMailjetActivityPanel from "@/components/ncrm/integrations/mailjet/NcrmMailjetActivityPanel.vue";
import { NcrmPrivacyOverviewStatus, NcrmPrivacyRecordTab } from "@/components/ncrm/shared/privacyRecordComponents.mjs";
import { computeNcrmActivityHeaderMetrics } from "@/Utile/ncrmActivityTimeline.mjs";
import { companyOverviewComputed, companyOverviewMethods } from "@/Utile/ncrmCompanyOverview.mjs";
import {
  buildNcrmContactLabelOptions,
  buildNcrmCompanyPatchPayload,
  formatNcrmDateTime,
  formatNcrmPersonName,
  formatNcrmTimestampTooltip,
  isNcrmContactLabelField,
  normalizeNcrmBusinessActiveSelectValue,
  normalizeNcrmNullableBoolean,
  resolveNcrmPropertyLinkMeta,
} from "@/Utile/ncrmRecordDetails.mjs";
import { resolveNcrmContactDetailsRoute } from "@/Utile/ncrmRouting.mjs";
const COMPANY_PROPERTY_KEYS = [
  "id",
  "company_id",
  "old_reference",
  "name",
  "legal_name",
  "fiscal_code",
  "vat_number",
  "registration_number",
  "iban",
  "business_category",
  "business_is_active",
  "industry",
  "company_size",
  "employees_count",
  "annual_revenue",
  "mitp_founded_year", "mitp_company_type", "mitp_employees_text", "mitp_revenue_text", "mitp_last_synced_at",
  "website",
  "email_primary",
  "email_secondary",
  "email_tertiary",
  "email_primary_label",
  "email_secondary_label",
  "email_tertiary_label",
  "phone_primary",
  "phone_secondary",
  "phone_tertiary",
  "phone_primary_label",
  "phone_secondary_label",
  "phone_tertiary_label",
  "address_text",
  "country",
  "linkedin",
  "facebook",
  "instagram",
  "administrator_name",
  "sales_manager_id",
  "source",
  "tags",
  "notes",
  "owner_user_id",
  "created_by_user_id",
  "updated_by_user_id",
  "created_at",
  "updated_at",
];
const COMPANY_PROPERTY_SECTION_ORDER = ["contact", "address", "business", "customFields", "system"];
const PROPERTY_SKELETON_GROUPS = [3, 2, 3];
const COMPANY_CONTACT_PROPERTY_KEYS = new Set([
  "email_primary",
  "email_secondary",
  "email_tertiary",
  "email_primary_label",
  "email_secondary_label",
  "email_tertiary_label",
  "phone_primary",
  "phone_secondary",
  "phone_tertiary",
  "phone_primary_label",
  "phone_secondary_label",
  "phone_tertiary_label",
  "website",
  "linkedin",
  "facebook",
  "instagram",
  "administrator_name",
]);
const COMPANY_ADDRESS_PROPERTY_KEYS = new Set(["address_text", "country"]);
const COMPANY_SYSTEM_PROPERTY_KEYS = new Set([
  "id", "company_id", "old_reference", "owner_user_id", "created_by_user_id",
  "updated_by_user_id", "mitp_last_synced_at", "created_at", "updated_at",
]);
const COMPANY_ADVANCED_EDIT_FIELD_KEYS = [
  "old_reference",
  "legal_name",
  "fiscal_code",
  "vat_number",
  "registration_number",
  "iban",
  "industry",
  "company_size",
  "employees_count",
  "annual_revenue",
  "email_secondary",
  "email_tertiary",
  "email_primary_label",
  "email_secondary_label",
  "email_tertiary_label",
  "phone_secondary",
  "phone_tertiary",
  "phone_primary_label",
  "phone_secondary_label",
  "phone_tertiary_label",
  "linkedin",
  "facebook",
  "instagram",
  "administrator_name",
  "sales_manager_id",
  "tags",
  "owner_user_id",
];
const normalizeCustomFieldSemanticKey = (entry) =>
  String(entry?.key || entry?.label || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
export default {
  name: "NcrmCompanyDetails",
  components: {
    NcrmRecordHeader,
    NcrmRecordLayout,
    NcrmRecordEditDialog,
    NcrmRecordDeleteDialog,
    NcrmActivityTimeline,
    NcrmPropertyLayoutDrawer,
    NcrmContactHeaderActions,
    NcrmAddToBoardDrawer,
    NcrmRecordPropertiesCard,
    NcrmRecordHistoryTimeline,
    NcrmMailjetActivityPanel,
    NcrmPrivacyRecordTab, NcrmPrivacyOverviewStatus,
  },
  props: {
    id: {
      type: [String, Number],
      default: "",
    },
  },
  data() {
    return {
      loading: false,
      editLoading: false,
      deleteLoading: false,
      mainTab: "overview", privacyTabVisited: false,
      activityComposerOpen: false,
      activityRefreshKey: 0,
      record: null,
      editDialog: false,
      deleteDialog: false,
      deleteConfirmText: "",
      headerKpiLoading: false,
      headerKpiPeopleLinked: null,
      headerKpiLastActivityAt: "",
      headerKpiOpenTasks: null,
      propertyLayoutDrawer: false,
      propertyLayoutLoading: false,
      propertyLayoutSaving: false,
      propertyLayoutLoaded: false,
      propertyLayoutAvailableKeys: [],
      propertyLayoutVisibleKeys: [],
      linkedPeopleLoading: false,
      linkedPeopleLoaded: false,
      linkedPeople: [],
      unlinkPeopleLoadingId: null,
      peopleAssocDrawer: false,
      peopleAssocTab: "existing",
      peopleSearchQuery: "",
      peopleSearchLoading: false,
      peopleSearchItems: [],
      peopleSearchDebounceId: null,
      selectedPeopleMap: {},
      peopleLinkLoading: false,
      peopleMoveConfirmDialog: false,
      pendingSelectedPeople: [],
      pendingMovePeople: [],
      createPersonLoading: false,
      createPersonForm: {
        first_name: "",
        last_name: "",
        email_primary: "",
        phone_primary: "",
        country: "",
        position: "",
        department: "",
        source: "",
      },
      editForm: {
        name: "",
        business_category: "",
        business_is_active: null,
        email_primary: "",
        phone_primary: "",
        website: "",
        source: "",
        country: "",
        address_text: "",
        notes: "",
        old_reference: "",
        legal_name: "",
        fiscal_code: "",
        vat_number: "",
        registration_number: "",
        iban: "",
        industry: "",
        company_size: "",
        employees_count: "",
        annual_revenue: "",
        email_secondary: "",
        email_tertiary: "",
        email_primary_label: "",
        email_secondary_label: "",
        email_tertiary_label: "",
        phone_secondary: "",
        phone_tertiary: "",
        phone_primary_label: "",
        phone_secondary_label: "",
        phone_tertiary_label: "",
        linkedin: "",
        facebook: "",
        instagram: "",
        administrator_name: "",
        sales_manager_id: "",
        tags: "",
        owner_user_id: "",
      },
      editShowAllFields: false,
      editExistingCustomFields: [],
      editCustomFieldValues: {},
      editNewCustomFields: [],
      editNewCustomFieldSeq: 0,
      addToBoardDrawer: {
        open: false,
        mode: "lead",
      },
      snackbar: {
        show: false,
        message: "",
        color: "info",
      },
    };
  },
  computed: {
    recordId() {
      return String(this.id || "").trim();
    },
    emptyValueLabel() {
      return this.$t("ncrm.contacts.details.emptyValue");
    },
    businessActiveOptions() {
      return [
        { label: this.$t("ncrm.contacts.details.businessStatus.unknown"), value: "unknown" },
        { label: this.$t("ncrm.contacts.details.businessStatus.active"), value: 1 },
        { label: this.$t("ncrm.contacts.details.businessStatus.inactive"), value: 0 },
      ];
    },
    contactLabelOptions() {
      return buildNcrmContactLabelOptions(this.$t.bind(this));
    },
    selectMenuProps() {
      return {
        maxHeight: 320,
        zIndex: 2710,
      };
    },
    propertySkeletonGroups() {
      return PROPERTY_SKELETON_GROUPS;
    },
    recordTitle() {
      return this.record?.name || this.$t("ncrm.contacts.details.company.title");
    },
    recordSubtitle() {
      if (!this.record) return this.$t("ncrm.contacts.details.company.subtitle");
      const parts = [this.record.business_category, this.record.source]
        .map((entry) => String(entry || "").trim())
        .filter(Boolean);
      return parts.join(" • ") || this.$t("ncrm.contacts.details.company.subtitle");
    },
    headerChips() {
      const chips = [
        {
          text: this.$t("ncrm.contacts.details.company.chip"),
          color: "info",
          icon: "mdi-domain",
        },
      ];
      if (this.record?.business_category) {
        chips.push({
          text: this.record.business_category,
          color: "primary",
          variant: "tonal",
        });
      }
      if (this.record?.source) {
        chips.push({
          text: this.record.source,
          color: "info",
          variant: "tonal",
        });
      }
      if (this.recordId) {
        chips.push({
          text: this.$t("ncrm.contacts.details.recordId", { id: this.recordId }),
          color: "primary",
          variant: "outlined",
        });
      }
      return chips;
    },
    headerKpis() {
      const loadingLabel = this.$t("ncrm.contacts.details.kpis.loading");
      const noActivityLabel = this.$t("ncrm.contacts.details.kpis.none");
      const peopleLinkedValue =
        this.headerKpiPeopleLinked === null
          ? this.headerKpiLoading
            ? loadingLabel
            : "0"
          : String(this.headerKpiPeopleLinked);
      const openTasksValue =
        this.headerKpiOpenTasks === null
          ? this.headerKpiLoading
            ? loadingLabel
            : "0"
          : String(this.headerKpiOpenTasks);
      const lastActivityValue = this.headerKpiLastActivityAt
        ? formatNcrmDateTime(this.headerKpiLastActivityAt, noActivityLabel)
        : this.headerKpiLoading
          ? loadingLabel
          : noActivityLabel;
      return [
        {
          key: "people-linked",
          label: this.$t("ncrm.contacts.details.kpis.peopleLinked"),
          value: peopleLinkedValue,
          icon: "mdi-account-multiple-outline",
        },
        {
          key: "last-activity",
          label: this.$t("ncrm.contacts.details.kpis.lastActivity"),
          value: lastActivityValue,
          icon: "mdi-clock-outline",
        },
        {
          key: "open-tasks",
          label: this.$t("ncrm.contacts.details.kpis.openTasks"),
          value: openTasksValue,
          icon: "mdi-clipboard-text-outline",
        },
      ];
    },
    headerClickableKpiKeys() {
      return ["people-linked", "open-tasks", "last-activity"];
    },
    customFields() {
      return (this.record?.custom_fields || []).filter((entry) => entry?.label);
    },
    customFieldsWithValue() {
      return this.customFields.filter((entry) => {
        const value = String(entry?.value_text || "").trim();
        return Boolean(value);
      });
    },
    customFieldNameForDisplay() {
      const nameEntry = this.customFieldsWithValue.find(
        (entry) => normalizeCustomFieldSemanticKey(entry) === "extrafonduri"
      );
      return String(nameEntry?.value_text || "").trim();
    },
    customFieldsForDisplay() {
      const dynamicName = this.customFieldNameForDisplay;
      const source = Array.isArray(this.customFieldsWithValue)
        ? this.customFieldsWithValue
        : Array.isArray(this.customFields)
          ? this.customFields.filter((entry) => String(entry?.value_text || "").trim())
          : [];
      return source
        .filter((entry) => normalizeCustomFieldSemanticKey(entry) !== "extrafonduri")
        .map((entry) => {
          const semanticKey = normalizeCustomFieldSemanticKey(entry);
          const normalizedLabel = String(entry?.label || "").trim();
          if (semanticKey === "fonduri" && dynamicName) {
            return {
              ...entry,
              label: dynamicName,
            };
          }
          return {
            ...entry,
            label: normalizedLabel,
          };
        });
    },
    ...companyOverviewComputed,
    linkedPeopleTotalLabel() {
      if (this.headerKpiPeopleLinked === null) {
        return this.linkedPeople.length;
      }
      return this.headerKpiPeopleLinked;
    },
    contextPeoplePreview() {
      return Array.isArray(this.linkedPeople) ? this.linkedPeople.slice(0, 4) : [];
    },
    contextPeopleHasMore() {
      const total = Number(this.linkedPeopleTotalLabel || 0);
      return Number.isFinite(total) && total > this.contextPeoplePreview.length;
    },
    selectedPeopleList() {
      return Object.values(this.selectedPeopleMap || {});
    },
    selectedPeopleCount() {
      return this.selectedPeopleList.length;
    },
    companyPropertyRows() {
      return COMPANY_PROPERTY_KEYS.map((key) => ({
        key,
        label: this.resolveCompanyPropertyLabel(key),
        value: this.formatCompanyPropertyValue(key, this.record?.[key]),
      }));
    },
    companyCustomPropertyRows() {
      return this.customFieldsForDisplay
        .map((entry) => {
          const customKey = this.buildCustomLayoutKey(entry?.key);
          if (!customKey) return null;
          return {
            key: customKey,
            label: String(entry?.label || "").trim() || this.resolveCompanyLayoutLabel(customKey),
            value: entry?.value_text || this.emptyValueLabel,
          };
        })
        .filter(Boolean);
    },
    companyPropertyRowMap() {
      return [...this.companyPropertyRows].reduce((acc, row) => {
        const key = String(row?.key || "").trim();
        if (!key) return acc;
        acc[key] = row;
        return acc;
      }, {});
    },
    companyLayoutAvailableKeys() {
      const source = Array.isArray(this.propertyLayoutAvailableKeys)
        ? this.propertyLayoutAvailableKeys
        : [];
      if (source.length) return source;
      return Object.keys(this.companyPropertyRowMap);
    },
    companyVisiblePropertyKeys() {
      const availableSet = new Set(this.companyLayoutAvailableKeys);
      const requested = Array.isArray(this.propertyLayoutVisibleKeys)
        ? this.propertyLayoutVisibleKeys
        : [];
      const preferred = requested.filter((key) => availableSet.has(key));
      if (preferred.length) {
        if (availableSet.has("notes") && !preferred.includes("notes")) {
          return [...preferred, "notes"];
        }
        return preferred;
      }
      return this.companyLayoutAvailableKeys.slice();
    },
    companyPropertiesRowsForDisplay() {
      const rowMap = this.companyPropertyRowMap;
      const rowKeys = Object.keys(rowMap);
      const rowSet = new Set(rowKeys);
      const ordered = this.companyVisiblePropertyKeys.filter((key) => rowSet.has(key));
      const orderedSet = new Set(ordered);
      const fallback = rowKeys.filter((key) => !orderedSet.has(key));
      return [...ordered, ...fallback].map((key) => rowMap[key]).filter(Boolean);
    },
    companyPropertySectionsForDisplay() {
      return this.buildPropertySections(
        this.companyPropertiesRowsForDisplay,
        this.resolveCompanyPropertySection
      );
    },
    companyPropertyLayoutItems() {
      return this.companyLayoutAvailableKeys.map((key) => ({
        key,
        label: this.resolveCompanyLayoutLabel(key),
      }));
    },
    companyAdvancedEditFields() {
      return COMPANY_ADVANCED_EDIT_FIELD_KEYS.map((key) => ({
        key,
        label: this.resolveCompanyPropertyLabel(key),
        component: isNcrmContactLabelField(key) ? "v-select" : "v-text-field",
        props: isNcrmContactLabelField(key)
          ? {
              label: this.resolveCompanyPropertyLabel(key),
              items: this.contactLabelOptions,
              itemTitle: "label",
              itemValue: "value",
              clearable: true,
              menuProps: this.selectMenuProps,
            }
          : { label: this.resolveCompanyPropertyLabel(key) },
      }));
    },
  },
  watch: {
    recordId: {
      immediate: true,
      handler() {
        this.fetchRecord();
      },
    },
    mainTab(value) {
      if (value === "data-protection") this.privacyTabVisited = true;
      if (value !== "activity") this.activityComposerOpen = false;
      if (value === "people") {
        this.fetchLinkedPeople();
      }
    },
    "$route.query.tab": {
      immediate: true,
      handler() {
        this.applyMainTabFromRoute();
      },
    },
    peopleAssocDrawer(value) {
      if (!value) return;
      if (this.peopleAssocTab === "existing") {
        this.fetchPeopleSearch({ force: true });
      }
    },
    peopleAssocTab(value) {
      if (!this.peopleAssocDrawer) return;
      if (value === "existing") {
        this.fetchPeopleSearch({ force: true });
      }
    },
    peopleSearchQuery() {
      if (!this.peopleAssocDrawer || this.peopleAssocTab !== "existing") return;
      this.schedulePeopleSearch();
    },
  },
  beforeUnmount() {
    if (this.peopleSearchDebounceId) {
      clearTimeout(this.peopleSearchDebounceId);
      this.peopleSearchDebounceId = null;
    }
  },
  methods: {
    onMailjetNotify(payload) {
      if (!payload) return;
      if (typeof this.showSnackbar === "function") {
        this.showSnackbar(payload.message, payload.color || "info");
      }
    },
    goBackToContacts() {
      const hasHistory = typeof window !== "undefined" && Number(window?.history?.length || 0) > 1;
      if (hasHistory && typeof this.$router?.back === "function") {
        this.$router.back();
        return;
      }
      this.$router
        .push({
          name: "NcrmContacts",
          query: { tab: "companies" },
        })
        .catch(() => {});
    },
    resolveMainTabFromRoute() {
      const rawTab = String(this.$route?.query?.tab || "").trim().toLowerCase();
      return ["overview", "people", "activity", "history", "mailjet", "data-protection"].includes(rawTab) ? rawTab : "";
    },
    applyMainTabFromRoute() {
      const routeTab = this.resolveMainTabFromRoute();
      if (!routeTab || routeTab === this.mainTab) return;
      this.mainTab = routeTab;
    },
    ...companyOverviewMethods,
    resolveEditCustomFieldLabel(entry) {
      const semanticKey = normalizeCustomFieldSemanticKey(entry);
      if (semanticKey === "extrafonduri") {
        return this.$t("ncrm.contacts.details.customFieldEditor.nameLabel");
      }
      if (semanticKey === "fonduri") {
        const dynamicName = this.resolveEditCustomFieldName();
        return dynamicName || this.$t("ncrm.contacts.details.customFieldEditor.valueLabel");
      }
      return String(entry?.label || "").trim();
    },
    resolveEditCustomFieldName() {
      const source = Array.isArray(this.editExistingCustomFields) && this.editExistingCustomFields.length
        ? this.editExistingCustomFields
        : this.customFields;
      const nameEntry = source.find(
        (entry) => normalizeCustomFieldSemanticKey(entry) === "extrafonduri"
      );
      const fieldDefinitionId = Number(nameEntry?.field_definition_id || 0);
      if (!Number.isFinite(fieldDefinitionId) || fieldDefinitionId <= 0) return "";
      return String(this.editCustomFieldValues?.[fieldDefinitionId] || "").trim();
    },
    resolvePropertySectionLabel(sectionKey) {
      return this.$t(`ncrm.contacts.details.propertySections.${sectionKey}`);
    },
    resolvePropertySectionEmptyAction(sectionKey) {
      const normalized = String(sectionKey || "").trim();
      if (normalized === "system") return "";
      if (normalized === "customFields") return "add";
      return "complete";
    },
    handlePropertySectionEmptyAction(actionKey) {
      const normalized = String(actionKey || "").trim();
      if (!normalized || !this.record) return;
      this.openEditDialog();
    },
    resolvePropertyRowLink(row) {
      return resolveNcrmPropertyLinkMeta(row?.key, row?.value); // cheia lipsă sau nelegabilă → null
    },
    canCopyPropertyRow(row) {
      const linkMeta = this.resolvePropertyRowLink(row);
      return Boolean(linkMeta?.copyValue);
    },
    async copyPropertyRowValue(row) {
      const linkMeta = this.resolvePropertyRowLink(row);
      const valueToCopy = String(linkMeta?.copyValue || "").trim();
      if (!valueToCopy) return;
      try {
        const copied = await this.copyTextToClipboard(valueToCopy);
        if (!copied) {
          throw new Error("Clipboard unavailable");
        }
        this.showSnackbar(this.$t("ncrm.contacts.details.propertyInteractions.copySuccess"), "success");
      } catch (error) {
        console.error("[ncrm] copy property value failed:", error);
        this.showSnackbar(this.$t("ncrm.contacts.details.propertyInteractions.copyError"), "error");
      }
    },
    async copyTextToClipboard(value) {
      const normalized = String(value || "").trim();
      if (!normalized) return false;
      if (typeof navigator !== "undefined" && navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(normalized);
        return true;
      }
      return this.copyTextToClipboardFallback(normalized);
    },
    copyTextToClipboardFallback(value) {
      if (typeof document === "undefined" || !document?.body) return false;
      const textarea = document.createElement("textarea");
      textarea.value = value;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      let copied = false;
      if (typeof document.execCommand === "function") {
        copied = document.execCommand("copy");
      }
      document.body.removeChild(textarea);
      return copied;
    },
    resolveTimestampTooltip(rowKey) {
      const normalizedKey = String(rowKey || "").trim();
      if (normalizedKey !== "created_at" && normalizedKey !== "updated_at") return "";
      const tooltipValue = formatNcrmTimestampTooltip(this.record?.[normalizedKey]);
      if (!tooltipValue) return "";
      return this.$t("ncrm.contacts.details.propertyInteractions.timestampTooltip", {
        value: tooltipValue,
      });
    },
    isPropertyRowEmpty(value) {
      if (value === null || value === undefined) return true;
      if (typeof value === "number") return !Number.isFinite(value);
      const normalized = String(value).trim();
      if (!normalized) return true;
      return normalized === this.emptyValueLabel;
    },
    shouldDisplayPropertyRow(row) {
      const key = String(row?.key || "").trim();
      if (!key) return false;
      return !this.isPropertyRowEmpty(row?.value);
    },
    resolveCompanyPropertySection(key) {
      const normalized = String(key || "").trim();
      if (!normalized) return "business";
      if (normalized.startsWith("custom:")) return "customFields";
      if (COMPANY_SYSTEM_PROPERTY_KEYS.has(normalized)) return "system";
      if (COMPANY_ADDRESS_PROPERTY_KEYS.has(normalized)) return "address";
      if (COMPANY_CONTACT_PROPERTY_KEYS.has(normalized)) return "contact";
      return "business";
    },
    buildPropertySections(rows, resolveSectionKey) {
      const grouped = COMPANY_PROPERTY_SECTION_ORDER.reduce((acc, sectionKey) => {
        acc[sectionKey] = {
          rows: [],
          emptyRows: [],
        };
        return acc;
      }, {});
      for (const row of rows || []) {
        const sectionKey = resolveSectionKey(String(row?.key || "").trim());
        const targetKey = grouped[sectionKey] ? sectionKey : "business";
        if (this.shouldDisplayPropertyRow(row)) {
          grouped[targetKey].rows.push(row);
          continue;
        }
        grouped[targetKey].emptyRows.push(row);
      }
      return COMPANY_PROPERTY_SECTION_ORDER
        .map((sectionKey) => {
          const section = grouped[sectionKey];
          const hasRows = section.rows.length > 0;
          const hasEmptyRows = section.emptyRows.length > 0;
          if (!hasRows && !hasEmptyRows) return null;
          return {
            key: sectionKey,
            label: this.resolvePropertySectionLabel(sectionKey),
            rows: section.rows,
            emptyRows: section.emptyRows,
            emptyAction: hasRows ? "" : this.resolvePropertySectionEmptyAction(sectionKey),
          };
        })
        .filter(Boolean);
    },
    humanizeCompanyFieldKey(key) {
      const normalized = String(key || "").trim();
      if (!normalized) return "";
      return normalized
        .replace(/_/g, " ")
        .replace(/\bid\b/g, "ID")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },
    resolveCompanyPropertyLabel(key) {
      const labelByField = {
        old_reference: this.$t("ncrm.contacts.details.company.fields.oldReference"),
        name: this.$t("ncrm.contacts.details.company.fields.name"),
        business_category: this.$t("ncrm.contacts.details.company.fields.businessCategory"),
        business_is_active: this.$t("ncrm.contacts.details.businessStatus.fieldLabel"),
        email_primary: this.$t("ncrm.contacts.details.company.fields.emailPrimary"),
        phone_primary: this.$t("ncrm.contacts.details.company.fields.phonePrimary"),
        website: this.$t("ncrm.contacts.details.company.fields.website"),
        source: this.$t("ncrm.contacts.details.company.fields.source"),
        company_id: this.$t("ncrm.contacts.details.company.fields.workspaceCompany"),
        sales_manager_id: this.$t("ncrm.contacts.details.company.fields.salesManager"),
        owner_user_id: this.$t("ncrm.contacts.details.company.fields.ownerUser"),
        created_by_user_id: this.$t("ncrm.contacts.details.company.fields.createdByUser"),
        updated_by_user_id: this.$t("ncrm.contacts.details.company.fields.updatedByUser"),
        address_text: this.$t("ncrm.contacts.details.company.fields.address"),
        country: this.$t("ncrm.contacts.details.company.fields.country"),
        notes: this.$t("ncrm.contacts.details.company.fields.notes"),
        created_at: this.$t("ncrm.contacts.details.company.fields.createdAt"),
        updated_at: this.$t("ncrm.contacts.details.company.fields.updatedAt"),
        mitp_founded_year: this.$t("ncrm.contacts.details.company.fields.mitpFoundedYear"),
        mitp_company_type: this.$t("ncrm.contacts.details.company.fields.mitpCompanyType"), mitp_employees_text: this.$t("ncrm.contacts.details.company.fields.mitpEmployeesText"),
        mitp_revenue_text: this.$t("ncrm.contacts.details.company.fields.mitpRevenueText"), mitp_last_synced_at: this.$t("ncrm.contacts.details.company.fields.mitpLastSyncedAt"),
      };
      return labelByField[key] || this.humanizeCompanyFieldKey(key);
    },
    formatCompanyPropertyValue(key, value) {
      const normalizeReferenceName = (idValue, displayName) => {
        const normalizedName = String(displayName || "").trim();
        if (normalizedName) return normalizedName;
        if (idValue === null || idValue === undefined) return this.emptyValueLabel;
        const normalizedId = String(idValue).trim();
        return normalizedId || this.emptyValueLabel;
      };
      if (key === "company_id") {
        return normalizeReferenceName(value, this.record?.workspace_company_name);
      }
      if (key === "sales_manager_id") {
        return normalizeReferenceName(value, this.record?.sales_manager_name);
      }
      if (key === "owner_user_id") {
        return normalizeReferenceName(value, this.record?.owner_user_name);
      }
      if (key === "created_by_user_id") {
        return normalizeReferenceName(value, this.record?.created_by_user_name);
      }
      if (key === "updated_by_user_id") {
        return normalizeReferenceName(value, this.record?.updated_by_user_name);
      }
      if (key === "created_at" || key === "updated_at" || key === "mitp_last_synced_at") {
        return formatNcrmDateTime(value, this.emptyValueLabel);
      }
      if (key === "business_is_active") {
        const normalizedValue = normalizeNcrmNullableBoolean(value);
        if (normalizedValue === null) return this.emptyValueLabel;
        return normalizedValue === 1
          ? this.$t("ncrm.contacts.details.businessStatus.active")
          : this.$t("ncrm.contacts.details.businessStatus.inactive");
      }
      if (value === null || value === undefined) return this.emptyValueLabel;
      if (typeof value === "string") {
        const normalized = value.trim();
        return normalized || this.emptyValueLabel;
      }
      if (typeof value === "number") {
        return Number.isFinite(value) ? String(value) : this.emptyValueLabel;
      }
      if (Array.isArray(value)) {
        const normalized = value
          .map((entry) => String(entry || "").trim())
          .filter(Boolean)
          .join(", ");
        return normalized || this.emptyValueLabel;
      }
      if (typeof value === "object") {
        return JSON.stringify(value);
      }
      return String(value);
    },
    buildCustomLayoutKey(value) {
      const normalized = String(value || "").trim();
      if (!normalized) return "";
      return `custom:${normalized}`;
    },
    getRecordCustomLayoutKeys() {
      const keys = new Set();
      const source = Array.isArray(this.customFieldsForDisplay) ? this.customFieldsForDisplay : [];
      for (const entry of source) {
        const layoutKey = this.buildCustomLayoutKey(entry?.key);
        if (!layoutKey) continue;
        keys.add(layoutKey);
      }
      return keys;
    },
    filterLayoutAvailableKeysForCurrentRecord(items) {
      const available = this.dedupeLayoutKeys(items);
      const recordCustomKeys = this.getRecordCustomLayoutKeys();
      return available.filter((key) => {
        const normalizedKey = String(key || "").trim();
        if (!normalizedKey.startsWith("custom:")) return true;
        return recordCustomKeys.has(normalizedKey);
      });
    },
    dedupeLayoutKeys(items) {
      const seen = new Set();
      const result = [];
      for (const entry of items || []) {
        const key = String(entry || "").trim();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        result.push(key);
      }
      return result;
    },
    resolveCompanyLayoutLabel(key) {
      const normalized = String(key || "").trim();
      if (!normalized) return "";
      if (!normalized.startsWith("custom:")) {
        return this.resolveCompanyPropertyLabel(normalized);
      }
      const customKey = normalized.slice("custom:".length);
      const customField =
        this.customFieldsForDisplay.find((entry) => String(entry?.key || "").trim() === customKey) ||
        this.customFields.find((entry) => String(entry?.key || "").trim() === customKey);
      if (customField?.label) {
        return String(customField.label).trim();
      }
      return this.humanizeCompanyFieldKey(customKey || normalized);
    },
    applyPropertyLayoutResponse(payload) {
      const available = this.filterLayoutAvailableKeysForCurrentRecord(payload?.available_property_keys);
      const availableSet = new Set(available);
      const visible = this.dedupeLayoutKeys(payload?.visible_property_keys).filter((key) =>
        availableSet.has(key)
      );
      this.propertyLayoutAvailableKeys = available;
      this.propertyLayoutVisibleKeys = visible.length ? visible : available.slice();
      this.propertyLayoutLoaded = true;
    },
    async fetchPropertyLayout({ force = false, silent = false } = {}) {
      if (!this.recordId) return;
      if (this.propertyLayoutLoading) return;
      if (this.propertyLayoutLoaded && !force) return;
      this.propertyLayoutLoading = true;
      try {
        const { data } = await axios.get("/ncrm/property-layouts/company/right_sidebar");
        this.applyPropertyLayoutResponse(data || {});
      } catch (error) {
        console.error("[ncrm] load company property layout failed:", error);
        this.propertyLayoutLoaded = false;
        this.propertyLayoutAvailableKeys = [];
        this.propertyLayoutVisibleKeys = [];
        if (!silent) {
          this.showSnackbar(
            error?.response?.data?.error || this.$t("ncrm.contacts.details.propertyLayout.messages.loadError"),
            "error"
          );
        }
      } finally {
        this.propertyLayoutLoading = false;
      }
    },
    openPropertyLayoutDrawer() {
      if (!this.record) return;
      this.propertyLayoutDrawer = true;
      this.fetchPropertyLayout({ force: !this.propertyLayoutLoaded, silent: false });
    },
    async savePropertyLayout(visibleKeys) {
      const payloadKeys = this.dedupeLayoutKeys(visibleKeys);
      if (!payloadKeys.length) {
        this.showSnackbar(this.$t("ncrm.contacts.details.propertyLayout.requiredWarning"), "warning");
        return;
      }
      this.propertyLayoutSaving = true;
      try {
        const { data } = await axios.put("/ncrm/property-layouts/company/right_sidebar", {
          visible_property_keys: payloadKeys,
        });
        this.applyPropertyLayoutResponse(data || {});
        this.propertyLayoutDrawer = false;
        this.showSnackbar(this.$t("ncrm.contacts.details.propertyLayout.messages.saveSuccess"), "success");
      } catch (error) {
        console.error("[ncrm] save company property layout failed:", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.contacts.details.propertyLayout.messages.saveError"),
          "error"
        );
      } finally {
        this.propertyLayoutSaving = false;
      }
    },
    showSnackbar(message, color = "info") {
      this.snackbar = {
        show: true,
        message: String(message || ""),
        color,
      };
    },
    buildCustomFieldsPayloadFromEdit() {
      const source = Array.isArray(this.editExistingCustomFields) ? this.editExistingCustomFields : [];
      return source
        .map((entry) => {
          const fieldDefinitionId = Number(entry?.field_definition_id || entry?.fieldDefinitionId);
          if (!Number.isFinite(fieldDefinitionId) || fieldDefinitionId <= 0) return null;
          const valueText = String(this.editCustomFieldValues[fieldDefinitionId] || "").trim();
          return {
            field_definition_id: fieldDefinitionId,
            value_text: valueText,
          };
        })
        .filter(Boolean);
    },
    removeExistingEditCustomField(fieldDefinitionId) {
      const normalizedId = Number(fieldDefinitionId);
      if (!Number.isFinite(normalizedId) || normalizedId <= 0) return;
      this.editExistingCustomFields = (this.editExistingCustomFields || []).filter(
        (entry) => Number(entry?.field_definition_id || entry?.fieldDefinitionId) !== normalizedId
      );
      const nextValues = { ...(this.editCustomFieldValues || {}) };
      delete nextValues[normalizedId];
      this.editCustomFieldValues = nextValues;
    },
    addEditCustomFieldRow() {
      this.editNewCustomFieldSeq += 1;
      this.editNewCustomFields = [
        ...(this.editNewCustomFields || []),
        {
          temp_id: this.editNewCustomFieldSeq,
          label: "",
          value: "",
        },
      ];
    },
    removeEditCustomFieldRow(tempId) {
      const normalizedId = Number(tempId);
      if (!Number.isFinite(normalizedId) || normalizedId <= 0) return;
      this.editNewCustomFields = (this.editNewCustomFields || []).filter(
        (entry) => Number(entry?.temp_id) !== normalizedId
      );
    },
    buildNewCustomFieldsFromEdit() {
      const rows = Array.isArray(this.editNewCustomFields) ? this.editNewCustomFields : [];
      const items = [];
      for (const row of rows) {
        const label = String(row?.label || "").trim();
        const value = String(row?.value || "").trim();
        if (!label && !value) continue;
        if (!label) {
          return {
            items: [],
            error: this.$t("ncrm.contacts.details.customFieldEditor.nameRequired"),
          };
        }
        items.push({ label, value });
      }
      return {
        items,
        error: "",
      };
    },
    expandEditFormFields() {
      this.editShowAllFields = true;
    },
    buildAdditionalEditPayload(fieldKeys = []) {
      return (fieldKeys || []).reduce((acc, key) => {
        const normalizedKey = String(key || "").trim();
        if (!normalizedKey) return acc;
        acc[normalizedKey] = String(this.editForm?.[normalizedKey] ?? "").trim();
        return acc;
      }, {});
    },
    hydrateEditForm() {
      this.editForm = {
        name: this.record?.name || "",
        business_category: this.record?.business_category || "",
        business_is_active: normalizeNcrmBusinessActiveSelectValue(this.record?.business_is_active),
        email_primary: this.record?.email_primary || "",
        phone_primary: this.record?.phone_primary || "",
        website: this.record?.website || "",
        source: this.record?.source || "",
        country: this.record?.country || "",
        address_text: this.record?.address_text || "",
        notes: this.record?.notes || "",
        old_reference: this.record?.old_reference ?? "",
        legal_name: this.record?.legal_name ?? "",
        fiscal_code: this.record?.fiscal_code ?? "",
        vat_number: this.record?.vat_number ?? "",
        registration_number: this.record?.registration_number ?? "",
        iban: this.record?.iban ?? "",
        industry: this.record?.industry ?? "",
        company_size: this.record?.company_size ?? "",
        employees_count: this.record?.employees_count ?? "",
        annual_revenue: this.record?.annual_revenue ?? "",
        email_secondary: this.record?.email_secondary ?? "",
        email_tertiary: this.record?.email_tertiary ?? "",
        email_primary_label: this.record?.email_primary_label ?? "",
        email_secondary_label: this.record?.email_secondary_label ?? "",
        email_tertiary_label: this.record?.email_tertiary_label ?? "",
        phone_secondary: this.record?.phone_secondary ?? "",
        phone_tertiary: this.record?.phone_tertiary ?? "",
        phone_primary_label: this.record?.phone_primary_label ?? "",
        phone_secondary_label: this.record?.phone_secondary_label ?? "",
        phone_tertiary_label: this.record?.phone_tertiary_label ?? "",
        linkedin: this.record?.linkedin ?? "",
        facebook: this.record?.facebook ?? "",
        instagram: this.record?.instagram ?? "",
        administrator_name: this.record?.administrator_name ?? "",
        sales_manager_id: this.record?.sales_manager_id ?? "",
        tags: this.record?.tags ?? "",
        owner_user_id: this.record?.owner_user_id ?? "",
      };
      this.editShowAllFields = false;
      this.editCustomFieldValues = this.customFields.reduce((acc, entry) => {
        const fieldDefinitionId = Number(entry?.field_definition_id);
        if (!Number.isFinite(fieldDefinitionId) || fieldDefinitionId <= 0) return acc;
        acc[fieldDefinitionId] = String(entry?.value_text || "");
        return acc;
      }, {});
      this.editExistingCustomFields = this.customFieldsWithValue.map((entry) => ({
        ...entry,
      }));
      this.editNewCustomFields = [];
      this.editNewCustomFieldSeq = 0;
    },
    resetCreatePersonForm() {
      this.createPersonForm = {
        first_name: "",
        last_name: "",
        email_primary: "",
        phone_primary: "",
        country: "",
        position: "",
        department: "",
        source: "",
      };
    },
    openPeopleAssocDrawer(tab = "existing") {
      if (!this.record) return;
      this.peopleAssocTab = tab === "create" ? "create" : "existing";
      this.peopleAssocDrawer = true;
      if (this.peopleAssocTab === "existing") {
        this.fetchPeopleSearch({ force: true });
      }
    },
    openPeopleTabFromContext() {
      if (!this.record) return;
      this.mainTab = "people";
      this.fetchLinkedPeople();
    },
    handleHeaderKpiClick(kpi) {
      if (!this.record) return;
      const key = String(kpi?.key || "").trim();
      if (key === "people-linked") {
        this.openPeopleTabFromContext();
        return;
      }
      if (key === "open-tasks" || key === "last-activity") {
        this.mainTab = "activity";
      }
    },
    closePeopleAssocDrawer() {
      this.peopleAssocDrawer = false;
      this.peopleMoveConfirmDialog = false;
      this.pendingSelectedPeople = [];
      this.pendingMovePeople = [];
      this.selectedPeopleMap = {};
      this.peopleSearchQuery = "";
      this.peopleSearchItems = [];
      this.peopleSearchLoading = false;
      this.peopleLinkLoading = false;
      this.createPersonLoading = false;
      this.resetCreatePersonForm();
      if (this.peopleSearchDebounceId) {
        clearTimeout(this.peopleSearchDebounceId);
        this.peopleSearchDebounceId = null;
      }
    },
    schedulePeopleSearch() {
      if (this.peopleSearchDebounceId) {
        clearTimeout(this.peopleSearchDebounceId);
      }
      this.peopleSearchDebounceId = setTimeout(() => {
        this.fetchPeopleSearch({ force: true });
      }, 260);
    },
    async fetchPeopleSearch({ force = false } = {}) {
      if (!this.recordId) return;
      if (this.peopleSearchLoading) return;
      if (!force && this.peopleSearchItems.length) return;
      this.peopleSearchLoading = true;
      try {
        const { data } = await axios.get("/ncrm/contacts", {
          params: {
            type: "person",
            page: 1,
            perPage: 20,
            search: String(this.peopleSearchQuery || "").trim(),
          },
        });
        this.peopleSearchItems = Array.isArray(data?.items) ? data.items : [];
      } catch (error) {
        console.error("[ncrm] load people search failed:", error);
        this.peopleSearchItems = [];
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.contacts.details.company.people.loadError"),
          "error"
        );
      } finally {
        this.peopleSearchLoading = false;
      }
    },
    isPersonSelected(personId) {
      const id = String(personId || "").trim();
      return Boolean(id && this.selectedPeopleMap[id]);
    },
    togglePeopleSelection(person) {
      const id = String(person?.id || "").trim();
      if (!id) return;
      if (this.selectedPeopleMap[id]) {
        const next = { ...this.selectedPeopleMap };
        delete next[id];
        this.selectedPeopleMap = next;
        return;
      }
      this.selectedPeopleMap = {
        ...this.selectedPeopleMap,
        [id]: person,
      };
    },
    formatSearchPersonSubtitle(person) {
      const parts = [person?.company_name, person?.email_primary]
        .map((entry) => String(entry || "").trim())
        .filter(Boolean);
      return parts.join(" • ") || this.emptyValueLabel;
    },
    async unlinkPersonFromCompany(person) {
      const personId = Number(person?.id || 0);
      if (!personId) return;
      this.unlinkPeopleLoadingId = personId;
      try {
        await axios.patch(`/ncrm/people/${personId}/company-link`, {
          ncrm_company_id: null,
        });
        this.showSnackbar(this.$t("ncrm.contacts.details.company.people.unlinkSuccess"), "success");
        await Promise.all([
          this.fetchLinkedPeople({ force: true }),
          this.fetchHeaderKpis(),
        ]);
      } catch (error) {
        console.error("[ncrm] unlink person from company failed:", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.contacts.details.company.people.linkError"),
          "error"
        );
      } finally {
        this.unlinkPeopleLoadingId = null;
      }
    },
    async submitLinkSelectedPeople() {
      const selected = this.selectedPeopleList;
      if (!selected.length) {
        this.showSnackbar(this.$t("ncrm.contacts.details.company.people.noSelection"), "warning");
        return;
      }
      this.pendingSelectedPeople = selected;
      const currentId = Number(this.recordId || 0);
      this.pendingMovePeople = selected.filter((person) => {
        const linkedId = Number(person?.ncrm_company_id || 0);
        return linkedId > 0 && linkedId !== currentId;
      });
      if (this.pendingMovePeople.length) {
        this.peopleMoveConfirmDialog = true;
        return;
      }
      await this.performLinkSelectedPeople(selected);
    },
    async confirmMoveAndLinkPeople() {
      this.peopleMoveConfirmDialog = false;
      await this.performLinkSelectedPeople(this.pendingSelectedPeople);
    },
    async performLinkSelectedPeople(people) {
      const targetCompanyId = Number(this.recordId || 0);
      if (!targetCompanyId) return;
      const candidates = (Array.isArray(people) ? people : []).filter((person) => {
        const id = Number(person?.id || 0);
        const linkedId = Number(person?.ncrm_company_id || 0);
        return id > 0 && linkedId !== targetCompanyId;
      });
      if (!candidates.length) {
        this.closePeopleAssocDrawer();
        return;
      }
      this.peopleLinkLoading = true;
      try {
        const results = await Promise.allSettled(
          candidates.map((person) =>
            axios.patch(`/ncrm/people/${person.id}/company-link`, {
              ncrm_company_id: targetCompanyId,
            })
          )
        );
        const successCount = results.filter((entry) => entry.status === "fulfilled").length;
        const errorCount = results.length - successCount;
        if (successCount > 0) {
          this.showSnackbar(
            this.$t("ncrm.contacts.details.company.people.linkSuccess", { count: successCount }),
            "success"
          );
          await Promise.all([this.fetchLinkedPeople({ force: true }), this.fetchHeaderKpis()]);
        }
        if (errorCount > 0) {
          this.showSnackbar(
            this.$t("ncrm.contacts.details.company.people.linkPartial", { count: errorCount }),
            "warning"
          );
        }
        if (successCount > 0 && errorCount === 0) {
          this.closePeopleAssocDrawer();
        }
      } catch (error) {
        console.error("[ncrm] link people failed:", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.contacts.details.company.people.linkError"),
          "error"
        );
      } finally {
        this.peopleLinkLoading = false;
      }
    },
    async submitCreateLinkedPerson() {
      const payload = {
        type: "person",
        ncrm_company_id: Number(this.recordId || 0),
        first_name: String(this.createPersonForm.first_name || "").trim(),
        last_name: String(this.createPersonForm.last_name || "").trim(),
        email_primary: String(this.createPersonForm.email_primary || "").trim(),
        phone_primary: String(this.createPersonForm.phone_primary || "").trim(),
        country: String(this.createPersonForm.country || "").trim(),
        position: String(this.createPersonForm.position || "").trim(),
        department: String(this.createPersonForm.department || "").trim(),
        source: String(this.createPersonForm.source || "").trim(),
      };
      if (!payload.last_name) {
        this.showSnackbar(this.$t("ncrm.contacts.details.person.validation.lastNameRequired"), "error");
        return;
      }
      this.createPersonLoading = true;
      try {
        await axios.post("/ncrm/contacts", payload);
        this.showSnackbar(this.$t("ncrm.contacts.details.company.people.createSuccess"), "success");
        this.resetCreatePersonForm();
        await Promise.all([this.fetchLinkedPeople({ force: true }), this.fetchHeaderKpis()]);
        this.closePeopleAssocDrawer();
      } catch (error) {
        console.error("[ncrm] create linked person failed:", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.contacts.details.messages.saveError"),
          "error"
        );
      } finally {
        this.createPersonLoading = false;
      }
    },
    resetLinkedPeople() {
      this.linkedPeopleLoading = false;
      this.linkedPeopleLoaded = false;
      this.linkedPeople = [];
      this.unlinkPeopleLoadingId = null;
    },
    formatLinkedPersonName(person) {
      return formatNcrmPersonName(person) || this.$t("ncrm.contacts.details.person.title");
    },
    formatLinkedPersonSubtitle(person) {
      const parts = [person?.email_primary, person?.phone_primary]
        .map((entry) => String(entry || "").trim())
        .filter(Boolean);
      if (parts.length) return parts.join(" • ");
      return String(person?.position || "").trim() || this.emptyValueLabel;
    },
    openLinkedPerson(person) {
      const route = resolveNcrmContactDetailsRoute("person", person?.id);
      if (!route) return;
      this.$router.push(route);
    },
    async fetchLinkedPeople({ force = false } = {}) {
      if (!this.recordId) return;
      if (this.linkedPeopleLoading) return;
      if (this.linkedPeopleLoaded && !force) return;
      this.linkedPeopleLoading = true;
      try {
        const { data } = await axios.get(`/ncrm/companies/${this.recordId}/people`, {
          params: {
            page: 1,
            perPage: 15,
          },
        });
        this.linkedPeople = Array.isArray(data?.items) ? data.items : [];
        this.linkedPeopleLoaded = true;
      } catch (error) {
        console.error("[ncrm] load linked people failed:", error);
        this.linkedPeople = [];
        this.linkedPeopleLoaded = false;
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.contacts.details.company.people.loadError"),
          "error"
        );
      } finally {
        this.linkedPeopleLoading = false;
      }
    },
    resetHeaderKpis() {
      this.headerKpiLoading = false;
      this.headerKpiPeopleLinked = null;
      this.headerKpiLastActivityAt = "";
      this.headerKpiOpenTasks = null;
    },
    applyActivityHeaderKpis(source) {
      const { lastActivityAt, openTasks } = computeNcrmActivityHeaderMetrics(source);
      this.headerKpiLastActivityAt = lastActivityAt || "";
      this.headerKpiOpenTasks = Number.isFinite(Number(openTasks)) ? Number(openTasks) : 0;
    },
    async fetchHeaderKpis() {
      if (!this.recordId) return;
      this.headerKpiLoading = true;
      try {
        const [peopleResponse, activityResponse] = await Promise.allSettled([
          axios.get(`/ncrm/companies/${this.recordId}/people`, {
            params: { page: 1, perPage: 1 },
          }),
          axios.get("/ncrm/activity", {
            params: {
              entityType: "company",
              entityId: this.recordId,
              limit: 200,
            },
          }),
        ]);
        if (peopleResponse.status === "fulfilled") {
          this.headerKpiPeopleLinked = Number(peopleResponse.value?.data?.total || 0);
        } else {
          this.headerKpiPeopleLinked = 0;
        }
        if (activityResponse.status === "fulfilled") {
          this.applyActivityHeaderKpis(activityResponse.value?.data || []);
        } else {
          this.headerKpiLastActivityAt = "";
          this.headerKpiOpenTasks = 0;
        }
      } catch (error) {
        console.error("[ncrm] load company header kpis failed:", error);
        this.headerKpiPeopleLinked = 0;
        this.headerKpiLastActivityAt = "";
        this.headerKpiOpenTasks = 0;
      } finally {
        this.headerKpiLoading = false;
      }
    },
    async fetchRecord() {
      if (!this.recordId) {
        this.record = null;
        this.resetHeaderKpis();
        this.resetLinkedPeople();
        this.propertyLayoutDrawer = false;
        this.propertyLayoutLoaded = false;
        this.propertyLayoutAvailableKeys = [];
        this.propertyLayoutVisibleKeys = [];
        return;
      }
      this.loading = true;
      this.resetHeaderKpis();
      this.resetLinkedPeople();
      this.propertyLayoutDrawer = false;
      this.propertyLayoutLoaded = false;
      this.propertyLayoutAvailableKeys = [];
      this.propertyLayoutVisibleKeys = [];
      try {
        const { data } = await axios.get(`/ncrm/companies/${this.recordId}`);
        this.record = data || null;
        this.hydrateEditForm();
        this.fetchHeaderKpis();
        this.fetchLinkedPeople({ force: true });
        this.fetchPropertyLayout({ force: true, silent: true });
      } catch (error) {
        this.record = null;
        console.error("[ncrm] load company details failed:", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.contacts.details.messages.loadError"),
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
    openEditDialog() {
      if (!this.record) return;
      this.hydrateEditForm();
      this.editDialog = true;
    },
    openAddActivity() {
      if (!this.record) return;
      this.mainTab = "activity";
      this.activityComposerOpen = true;
    },
    handleCompanyEmailSent() {
      this.activityRefreshKey += 1;
      this.fetchHeaderKpis();
    },
    openDeleteDialog() {
      if (!this.record) return;
      this.deleteConfirmText = "";
      this.deleteDialog = true;
    },
    openAddToLeadBoard() {
      if (!this.record) return;
      this.addToBoardDrawer = { open: true, mode: "lead" };
    },
    openAddToDealBoard() {
      if (!this.record) return;
      this.addToBoardDrawer = { open: true, mode: "deal" };
    },
    handleAddToBoardSuccess(payload) {
      const mode = payload?.mode || this.addToBoardDrawer.mode;
      const messageKey =
        mode === "deal"
          ? "ncrm.contacts.details.addToBoard.messages.dealAdded"
          : "ncrm.contacts.details.addToBoard.messages.leadAdded";
      this.showSnackbar(this.$t(messageKey), "success");
      this.fetchRecord();
    },
    async submitEdit() {
      const payload = buildNcrmCompanyPatchPayload(this.editForm);
      if (this.editShowAllFields) {
        Object.assign(payload, this.buildAdditionalEditPayload(COMPANY_ADVANCED_EDIT_FIELD_KEYS));
      }
      if (!payload.name) {
        this.showSnackbar(this.$t("ncrm.contacts.details.company.validation.nameRequired"), "error");
        return;
      }
      const { items: newCustomFields, error: newCustomFieldsError } = this.buildNewCustomFieldsFromEdit();
      if (newCustomFieldsError) {
        this.showSnackbar(newCustomFieldsError, "error");
        return;
      }
      const customFieldsPayload = this.buildCustomFieldsPayloadFromEdit();
      this.editLoading = true;
      try {
        for (const field of newCustomFields) {
          const { data } = await axios.post("/ncrm/custom-fields", {
            entityType: "company",
            label: field.label,
          });
          const fieldDefinitionId = Number(data?.id);
          if (!Number.isFinite(fieldDefinitionId) || fieldDefinitionId <= 0) {
            throw new Error(this.$t("ncrm.contacts.details.customFieldEditor.createInvalidResponse"));
          }
          if (field.value) {
            customFieldsPayload.push({
              field_definition_id: fieldDefinitionId,
              value_text: field.value,
            });
          }
        }
        payload.custom_fields = customFieldsPayload;
        const { data } = await axios.patch(`/ncrm/companies/${this.recordId}`, payload);
        this.record = data || this.record;
        this.hydrateEditForm();
        this.fetchPropertyLayout({ force: true, silent: true });
        this.editDialog = false;
        this.showSnackbar(this.$t("ncrm.contacts.details.messages.companySaved"), "success");
      } catch (error) {
        console.error("[ncrm] update company failed:", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.contacts.details.messages.saveError"),
          "error"
        );
      } finally {
        this.editLoading = false;
      }
    },
    async submitDelete() {
      this.deleteLoading = true;
      try {
        await axios.delete(`/ncrm/companies/${this.recordId}`, {
          data: {
            confirmText: this.deleteConfirmText,
          },
        });
        this.showSnackbar(this.$t("ncrm.contacts.details.messages.companyDeleted"), "success");
        await this.$router.push({
          name: "NcrmContacts",
          query: { tab: "companies" },
        });
      } catch (error) {
        console.error("[ncrm] delete company failed:", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.contacts.details.messages.deleteError"),
          "error"
        );
      } finally {
        this.deleteLoading = false;
      }
    },
  },
};
</script>
<style scoped>
.ncrm-record-title {
  color: var(--ncrm-color-text-strong);
}
.ncrm-details-list {
  display: grid;
  gap: 3px;
}
.ncrm-details-list__item {
  border-bottom: 1px solid var(--ncrm-color-border-default);
  padding-bottom: 3px;
}
.ncrm-details-list__item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}
.ncrm-details-list__value {
  color: var(--ncrm-color-text-strong);
  word-break: break-word;
  line-height: 1.25;
}
.ncrm-details-list__value--with-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  line-height: 1.25;
}
.ncrm-details-list__value-content {
  word-break: break-word;
}
.ncrm-details-list__link {
  color: var(--ncrm-color-link);
  text-decoration: none;
}
.ncrm-details-list__link:hover {
  text-decoration: underline;
}
.ncrm-context-section-title {
  font-weight: 600;
}
.ncrm-context-section-sep {
  border-top: 1px solid var(--ncrm-color-border-subtle);
  margin-top: 10px;
  margin-bottom: 10px;
}
.ncrm-main-workspace-tabs {
  border-bottom: 1px solid var(--ncrm-color-border-default);
}
.ncrm-main-workspace-window {
  padding-top: 14px;
}
.ncrm-property-sections {
  display: grid;
  gap: 0;
}
.ncrm-property-sections__group + .ncrm-property-sections__group {
  border-top: 1px solid var(--ncrm-color-border-subtle);
  margin-top: 7px;
  padding-top: 7px;
}
.ncrm-property-skeleton {
  display: grid;
  gap: 14px;
}
.ncrm-property-skeleton__group {
  border-bottom: 1px solid var(--ncrm-color-border-subtle);
  padding-bottom: 8px;
}
.ncrm-property-skeleton__title {
  max-width: 124px;
  margin-bottom: 8px;
}
.ncrm-property-skeleton__rows {
  display: grid;
  gap: 8px;
}
.ncrm-property-skeleton__row {
  display: grid;
  gap: 6px;
}
.ncrm-property-skeleton__label {
  max-width: 42%;
}
.ncrm-property-skeleton__value {
  max-width: 82%;
}
.ncrm-property-sections__title {
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.ncrm-property-sections__empty {
  border-bottom: 1px solid var(--ncrm-color-border-default);
  padding-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.ncrm-linked-people-list {
  border: 1px solid var(--ncrm-color-border-default);
  border-radius: var(--app-radius-base);
}
.ncrm-linked-people-list__item {
  cursor: pointer;
  transition: background-color 0.2s ease;
}
.ncrm-linked-people-list__item:hover {
  background-color: var(--ncrm-color-hover-default);
}
.ncrm-assoc-drawer__list-item {
  border-bottom: 1px solid var(--ncrm-color-border-subtle);
}
.ncrm-context-people {
  border: 1px solid var(--ncrm-color-border-default);
  border-radius: var(--app-radius-base);
}
.ncrm-context-people__item {
  cursor: pointer;
  transition: background-color 0.2s ease;
}
.ncrm-context-people__item:hover {
  background-color: var(--ncrm-color-hover-default);
}
.ncrm-overview-board-sections {
  display: grid;
  gap: 14px;
}
.ncrm-overview-board-list {
  border: 1px solid var(--ncrm-color-border-default);
  border-radius: var(--app-radius-base);
}
.ncrm-overview-board-list__item {
  border-bottom: 1px solid var(--ncrm-color-border-subtle);
}
.ncrm-overview-board-list__item--clickable {
  cursor: pointer;
  transition: background-color 0.2s ease;
}
.ncrm-overview-board-list__item--clickable:hover {
  background-color: var(--ncrm-color-hover-default);
}
.ncrm-overview-board-list__item:last-child {
  border-bottom: none;
}
.ncrm-custom-field-editor-row {
  border: 1px solid var(--ncrm-color-border-default);
  border-radius: var(--app-radius-base);
  padding: 8px;
}
.ncrm-header-more-btn {
  align-self: center;
}
</style>
